import {queryOptions, useQuery, type QueryKey} from '@github-ui/react-query'
import {reactFetch} from '@github-ui/verified-fetch'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'

export type DescriptionEditInformation = {
  lastUserContentEdit: {
    editor: {
      login: string
      url: string
    } | null
  } | null
  viewerCanReadUserContentEdits: boolean
}

export type DescriptionData = {
  body?: string
  bodyHtml: string
  bodyVersion?: string
  editInformation?: DescriptionEditInformation
}

type UseDescriptionDataOptions = {
  /** Optional initial bodyHtml to seed the query before the first fetch resolves. */
  initialBodyHtml?: string

  /** Optional initial bodyText to seed the query before the first fetch resolves. */
  initialBodyText?: string

  /** Optional edit information from the initial overview payload. */
  initialEditInformation?: DescriptionData['editInformation']
}

function descriptionQueryOptions(pathName: string) {
  return queryOptions({
    queryKey: [PageData.description, pathName],
    queryFn: async () => {
      const result = await reactFetch(pathName)
      if (!result.ok) throw new Error(`HTTP ${result.status}`)
      const json = await result.json()
      return json as DescriptionData
    },
    staleTime: Infinity,
  })
}

export function descriptionQueryKey(pathName: string): QueryKey {
  return descriptionQueryOptions(pathName).queryKey
}

export function useDescriptionData(options: UseDescriptionDataOptions = {}) {
  const descriptionURL = usePageDataUrl(PageData.description)

  return useQuery({
    ...descriptionQueryOptions(descriptionURL),
    ...(options.initialBodyHtml !== undefined && options.initialBodyText !== undefined
      ? {
          initialData: {
            bodyHtml: options.initialBodyHtml,
            body: options.initialBodyText,
            editInformation: options.initialEditInformation,
          },
        }
      : {}),
  })
}
